import { useMemo, useState } from 'react';

import { ChartNoAxesCombinedIcon, InfoIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Label, PolarAngleAxis, PolarRadiusAxis, RadialBar, RadialBarChart } from 'recharts';

import { ExportButton } from '@/components/atoms/ExportButton/ExportButton';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer, type ChartConfig } from '@/components/ui/chart';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

import {
  METRIC_CHART_CONFIGS,
  type DurationMetricData,
  type MetricKey,
} from './overviewChartConfig';
import { useComparisonOverviewData } from '../../hooks/useComparisonOverviewData';
import { useOverviewExport } from '../../hooks/useOverviewExport';
import { useOverviewResponseTimes } from '../../hooks/useOverviewResponseTimes';
import { useOverviewSessionMetrics } from '../../hooks/useOverviewSessionMetrics';
import { useOverviewTimeseries } from '../../hooks/useOverviewTimeseries';
import { selectAnalyticsDates } from '../../redux/selectors';
import { formatDateRangeLabel, isSingleDayRange } from '../../utils/formatDateRangeLabel';
import { formatDurationFromSeconds } from '../../utils/formatDuration';
import { getComparisonDates } from '../../utils/getComparisonDates';
import { getComparisonPeriodKey, PercentChangeIndicator } from '../aiAgents/PercentChangeIndicator';
import { KpiTile } from '../KpiTile/KpiTile';
import { TimeseriesChart } from '../TimeseriesChart/TimeseriesChart';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CHART_COLOR = 'var(--chart-1)';

/**
 * Sub-minute precision for response times, shared with the AI Agents page so the
 * same value reads the same on both (3.5s, not 4s here and 3.5s there).
 */
const RESPONSE_TIME_DECIMALS = 1;

// ---------------------------------------------------------------------------
// Utility functions
// ---------------------------------------------------------------------------

/**
 * Formats a duration in minutes to a human-readable string (e.g. "2m 30s").
 * Returns "–" for null/NaN. `subMinuteDecimals` sets the precision below one
 * minute: response times keep one decimal so a 3.5s reply doesn't read as "4s",
 * matching the AI Agents page. Session lengths stay on whole seconds.
 */
const formatDuration = (minutes: number | null, subMinuteDecimals = 0): string => {
  if (minutes == null || isNaN(minutes)) {
    return '–';
  }
  return formatDurationFromSeconds(minutes * 60, subMinuteDecimals);
};

/** Formats a CSAT score as "X.X/5". Returns "–" for null. */
const formatCsat = (value: number | null): string => {
  if (value == null) {
    return '–';
  }
  return `${value.toFixed(1)}/5`;
};

/** Formats a 0–1 containment rate as a percentage string (e.g. "75%"). Returns "–" for null. */
const formatContainment = (rate: number | null): string => {
  if (rate == null) {
    return '–';
  }
  return `${Math.round(rate * 100)}%`;
};

/**
 * Formats a count compactly for tight spaces like the donut center (e.g. 1088537 -> "1.09M").
 * Numbers below 1000 stay exact. Exact values are still shown in the adjacent stat list.
 */
const formatCompactNumber = (value: number): string =>
  value.toLocaleString(undefined, { notation: 'compact', maximumFractionDigits: 2 });

/** Formats a numeric count with locale-aware thousand separators. Returns "–" for null. */
const formatCount = (count: number | null): string => {
  if (count == null) {
    return '–';
  }
  return count.toLocaleString();
};

/**
 * Computes a weighted median session length in minutes.
 * Uses the containment rate to blend AI-only and human-involved session durations.
 * Falls back to whichever median is available if only one exists.
 */
const computeWeightedSessionMinutes = (metrics: {
  median_ai_session_length_seconds?: number | null;
  median_human_session_length_seconds?: number | null;
  containment_rate?: number | null;
}): number | null => {
  const toMin = (s: number | null | undefined) => (s != null ? s / 60 : null);
  const ai = toMin(metrics.median_ai_session_length_seconds);
  const human = toMin(metrics.median_human_session_length_seconds);
  const containmentRate = metrics.containment_rate ?? 0;
  if (ai != null && human != null) {
    return ai * containmentRate + human * (1 - containmentRate);
  }
  return ai ?? human ?? null;
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const ConversationStatRow = ({
  label,
  value,
  tooltip,
  indented,
  bold,
}: {
  label: string;
  value: string;
  tooltip?: string;
  indented?: boolean;
  bold?: boolean;
}) => (
  <div className={cn('flex items-center justify-between', indented && 'pl-6')}>
    <span
      className={cn(
        'flex items-center gap-1.5 text-sm text-foreground-muted',
        bold ? 'font-semibold' : ''
      )}
    >
      {label}
      {tooltip && (
        <InfoTooltip content={tooltip} size="large">
          <InfoIcon className="size-3.5 text-foreground-muted" />
        </InfoTooltip>
      )}
    </span>
    <span className="text-sm leading-none font-medium">{value}</span>
  </div>
);

const SectionError = ({ onRetry, t }: { onRetry: () => void; t: (key: string) => string }) => (
  <div className="flex flex-col items-center justify-center gap-2 py-6 text-center">
    <p className="text-sm text-foreground-muted">{t('common.something_wrong')}</p>
    <Button variant="outline-neutral" size="sm" onClick={onRetry}>
      {t('common.reload')}
    </Button>
  </div>
);

const SectionEmptyState = ({ t }: { t: (key: string) => string }) => (
  <div className="flex flex-col items-center justify-center gap-3 py-10">
    <div className="flex items-center gap-2 rounded-xl bg-bg-muted p-2">
      <ChartNoAxesCombinedIcon className="size-6 text-foreground-muted" />
    </div>
    <p className="text-2xl leading-none font-semibold">{t('analytics.overview_new.no_data')}</p>
    <p className="text-sm leading-5 font-normal text-foreground-muted">
      {t('analytics.overview_new.no_data_subtitle')}
    </p>
  </div>
);

interface ComparisonIndicatorProps {
  current: number | null;
  previous: number | null;
  isLoading: boolean;
  label: string;
}

const DurationMetricSection = ({
  titleKey,
  data,
  t,
  isLoading,
  error,
  onRetry,
  comparison,
}: {
  titleKey: string;
  data: DurationMetricData | null;
  t: (key: string) => string;
  isLoading: boolean;
  error?: boolean;
  onRetry?: () => void;
  comparison?: ComparisonIndicatorProps;
}) => {
  const renderContent = () => {
    if (isLoading) {
      return (
        <>
          <Skeleton className="h-9 w-20" />
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-full" />
        </>
      );
    }

    if (error && onRetry) {
      return <SectionError onRetry={onRetry} t={t} />;
    }

    if (!data) {
      return <p className="text-sm text-foreground-muted">–</p>;
    }

    return (
      <>
        <div className="flex items-baseline gap-2">
          <p className="text-2xl leading-none font-semibold">{data.total}</p>
          {comparison && (
            <PercentChangeIndicator
              current={comparison.current}
              previous={comparison.previous}
              isLoading={comparison.isLoading}
              comparisonLabel={comparison.label}
              invertColors
            />
          )}
        </div>
        <div className="ml-2 flex flex-col gap-3 border-l border-stroke pl-4">
          {data.breakdown.map((item) => (
            <div key={item.labelKey} className="flex items-center justify-between">
              <span className="text-sm leading-none font-medium">{t(item.labelKey)}</span>
              <span className="text-sm leading-none font-medium">{item.value}</span>
            </div>
          ))}
        </div>
      </>
    );
  };

  return (
    <div className="space-y-3">
      <p className="text-xs leading-4 font-medium text-foreground-muted">{t(titleKey)}</p>
      {renderContent()}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Skeleton loaders
// ---------------------------------------------------------------------------

const OverviewCardSkeleton = ({ showChart = true }: { showChart?: boolean }) => (
  <Card className="rounded-lg bg-background text-foreground shadow-none">
    <CardHeader>
      <Skeleton className="h-6 w-32" />
      <Skeleton className="h-4 w-48" />
    </CardHeader>
    <CardContent className="space-y-6">
      <div className="grid grid-cols-3 gap-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-20 rounded-lg" />
        ))}
      </div>
      {showChart && <Skeleton className="aspect-4/1 w-full rounded-lg" />}
    </CardContent>
  </Card>
);

const MeaningfulCardSkeleton = () => (
  <Card className="rounded-lg bg-background text-foreground shadow-none">
    <CardHeader>
      <Skeleton className="h-6 w-48" />
      <Skeleton className="h-4 w-32" />
    </CardHeader>
    <CardContent>
      <div className="flex items-center gap-12">
        <Skeleton className="size-52 shrink-0 rounded-full" />
        <div className="flex flex-1 flex-col gap-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-5 w-full" />
          ))}
        </div>
      </div>
    </CardContent>
  </Card>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const AnalyticsOverview = () => {
  const { t } = useTranslation();
  const { startDate, endDate } = useSelector(selectAnalyticsDates);

  const isSingleDay = isSingleDayRange(startDate, endDate);
  const dateRange = formatDateRangeLabel(startDate, endDate);

  const [selectedMetric, setSelectedMetric] = useState<MetricKey>('totalConversations');
  const comparisonLabel = t(getComparisonPeriodKey(startDate, endDate));

  // Drive the chart x-axis from the selected range (and the previous-period
  // range when comparing), so every day renders even if it returned no data.
  const period = useMemo(() => ({ start: startDate, end: endDate }), [startDate, endDate]);
  const comparisonPeriod = useMemo(() => {
    const { startDate: start, endDate: end } = getComparisonDates(startDate, endDate);
    return { start, end };
  }, [startDate, endDate]);

  // ── Data fetching ────────────────────────────────────────────────────────
  const sessionMetrics = useOverviewSessionMetrics();
  const responseTimes = useOverviewResponseTimes();
  const timeseries = useOverviewTimeseries(selectedMetric, isSingleDay);
  const comparison = useComparisonOverviewData(selectedMetric, isSingleDay);
  const { exportWidget, isExporting } = useOverviewExport();

  const sessionMetricsData = sessionMetrics.data;

  // ── Derived data ─────────────────────────────────────────────────────────

  const comparisonSessionMetrics = comparison.sessionMetrics.data;

  const kpiTiles = useMemo(
    () => [
      {
        key: 'totalConversations' as MetricKey,
        labelKey: 'analytics.total_conversations',
        tooltipKey: 'analytics.overview_new.total_conversations_tooltip',
        value: formatCount(sessionMetricsData?.total_conversations ?? null),
        rawCurrent: sessionMetricsData?.total_conversations,
        rawPrevious: comparisonSessionMetrics?.total_conversations,
      },
      {
        key: 'csat' as MetricKey,
        labelKey: 'analytics.overview_new.avg_ai_csat',
        tooltipKey: 'analytics.overview_new.avg_ai_csat_tooltip',
        value: formatCsat(sessionMetricsData?.avg_ai_csat ?? null),
        rawCurrent: sessionMetricsData?.avg_ai_csat,
        rawPrevious: comparisonSessionMetrics?.avg_ai_csat,
      },
      {
        key: 'containment' as MetricKey,
        labelKey: 'analytics.containment',
        tooltipKey: 'analytics.overview_new.containment_tooltip',
        value: formatContainment(sessionMetricsData?.containment_rate ?? null),
        rawCurrent: sessionMetricsData?.containment_rate,
        rawPrevious: comparisonSessionMetrics?.containment_rate,
      },
    ],
    [sessionMetricsData, comparisonSessionMetrics]
  );

  const currentConfig = METRIC_CHART_CONFIGS[selectedMetric];

  const chartConfig = useMemo(
    () => ({ value: { label: t(currentConfig.label), color: CHART_COLOR } }) satisfies ChartConfig,
    [currentConfig, t]
  );

  const meaningfulData = useMemo(() => {
    if (!sessionMetricsData) {
      return null;
    }
    const total = sessionMetricsData.total_conversations ?? 0;
    if (total === 0) {
      return null;
    }
    const meaningful = sessionMetricsData.meaningful_conversations ?? 0;
    const nonMeaningful = sessionMetricsData.nonmeaningful_conversations ?? 0;
    const workflow = sessionMetricsData.meaningful_brain_conversations ?? 0;
    const genAi = sessionMetricsData.meaningful_collection_conversations ?? 0;
    const percentage = total > 0 ? Math.round((meaningful / total) * 100) : 0;

    return { total, meaningful, nonMeaningful, workflow, genAi, percentage };
  }, [sessionMetricsData]);

  const isOverviewEmpty =
    !sessionMetrics.isLoading &&
    (!!sessionMetrics.error || (sessionMetricsData?.total_conversations ?? -1) === 0);
  const isMeaningfulEmpty =
    !sessionMetrics.isLoading && (!!sessionMetrics.error || !meaningfulData);

  const responseTimeData = useMemo((): DurationMetricData | null => {
    const rt = responseTimes.data;
    if (!rt) {
      return null;
    }
    return {
      total: formatDuration(rt.avg_response_time, RESPONSE_TIME_DECIMALS),
      breakdown: [
        {
          labelKey: 'analytics.overview_new.ai_agents',
          value: formatDuration(rt.avg_ai_response_time, RESPONSE_TIME_DECIMALS),
        },
        {
          labelKey: 'analytics.overview_new.human_agents',
          value: formatDuration(rt.avg_human_response_time, RESPONSE_TIME_DECIMALS),
        },
      ],
    };
  }, [responseTimes.data]);

  // Overall median session length: weighted average using containment as proxy
  // for AI-only vs human-involved session split.
  const currentSessionLengthMinutes = useMemo(
    () => computeWeightedSessionMinutes(sessionMetricsData ?? {}),
    [sessionMetricsData]
  );

  const sessionLengthData = useMemo((): DurationMetricData | null => {
    if (!sessionMetricsData) {
      return null;
    }

    const toMinutes = (seconds: number | null | undefined): number | null =>
      seconds != null ? seconds / 60 : null;

    return {
      total: formatDuration(currentSessionLengthMinutes),
      breakdown: [
        {
          labelKey: 'analytics.overview_new.ai_agent_sessions',
          value: formatDuration(toMinutes(sessionMetricsData.median_ai_session_length_seconds)),
        },
        {
          labelKey: 'analytics.overview_new.human_agent_sessions',
          value: formatDuration(toMinutes(sessionMetricsData.median_human_session_length_seconds)),
        },
      ],
    };
  }, [sessionMetricsData, currentSessionLengthMinutes]);

  const comparisonSessionLengthMinutes = useMemo(
    () => computeWeightedSessionMinutes(comparisonSessionMetrics ?? {}),
    [comparisonSessionMetrics]
  );

  const isTimeBasedEmpty =
    !responseTimes.isLoading &&
    !sessionMetrics.isLoading &&
    (!!responseTimes.error || !!sessionMetrics.error || (!responseTimeData && !sessionLengthData));

  // ── Full-page skeleton while all queries are in-flight ───────────────────

  if (sessionMetrics.isLoading && responseTimes.isLoading) {
    return (
      <div className="mx-auto max-w-6xl space-y-6">
        <OverviewCardSkeleton showChart={!isSingleDay} />
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <MeaningfulCardSkeleton />
          </div>
          <Card className="rounded-lg bg-background text-foreground shadow-none">
            <CardHeader>
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-32" />
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <Skeleton className="h-4 w-36" />
                <Skeleton className="h-9 w-20" />
                <Skeleton className="h-5 w-full" />
                <Skeleton className="h-5 w-full" />
              </div>
              <div className="space-y-3">
                <Skeleton className="h-4 w-36" />
                <Skeleton className="h-9 w-20" />
                <Skeleton className="h-5 w-full" />
                <Skeleton className="h-5 w-full" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      {/* ── Overview Card ─────────────────────────────────────────────── */}
      <Card className="rounded-lg bg-background text-foreground shadow-none">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex flex-col gap-2">
              <CardTitle>{t('analytics.overview')}</CardTitle>
              <CardDescription>{dateRange}</CardDescription>
            </div>
            {!isOverviewEmpty && (
              <ExportButton
                variant="icon"
                onExport={(format) => exportWidget('overview', format)}
                disabled={!sessionMetrics.data && timeseries.data.length === 0}
                isExporting={isExporting}
              />
            )}
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* KPI Score Cards (3-up) */}
          {sessionMetrics.error ? (
            <SectionError onRetry={sessionMetrics.onRefetch} t={t} />
          ) : (
            <div className="grid grid-cols-3 gap-4">
              {kpiTiles.map((tile) => (
                <KpiTile
                  key={tile.key}
                  label={t(tile.labelKey)}
                  tooltip={t(tile.tooltipKey)}
                  value={tile.value}
                  isLoading={sessionMetrics.isLoading}
                  isSelected={selectedMetric === tile.key}
                  onClick={() => setSelectedMetric(tile.key)}
                  comparison={
                    comparison.isComparing && (
                      <PercentChangeIndicator
                        current={tile.rawCurrent}
                        previous={tile.rawPrevious}
                        isLoading={comparison.sessionMetrics.isLoading}
                        comparisonLabel={comparisonLabel}
                        className="mt-1"
                      />
                    )
                  }
                />
              ))}
            </div>
          )}

          {/* Line Chart – only shown for multi-day ranges */}
          {!isSingleDay &&
            (sessionMetricsData?.total_conversations === 0 ? (
              <SectionEmptyState t={t} />
            ) : (
              /* The csat hover reads each point's own csatBreakdown, attached
                 per day by useOverviewTimeseries. No period-level breakdown is
                 passed on purpose — that is what froze the hover values while
                 the hovered date changed. */
              <TimeseriesChart
                timeseries={timeseries}
                chartConfig={chartConfig}
                currentConfig={currentConfig}
                t={t}
                period={period}
                comparisonPeriod={comparisonPeriod}
                comparisonTimeseries={comparison.timeseries}
                isComparing={comparison.isComparing}
              />
            ))}
        </CardContent>
      </Card>

      {/* ── Bottom Section ────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Meaningful Conversations Card */}
        <Card className="rounded-lg bg-background text-foreground shadow-none lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex flex-col gap-2">
                <CardTitle className="flex items-center gap-1.5">
                  {t('analytics.meaningful')}
                  <InfoTooltip content={t('analytics.meaningful_tooltip')} size="large">
                    <InfoIcon className="size-4 text-foreground-muted" />
                  </InfoTooltip>
                </CardTitle>
                <CardDescription>{dateRange}</CardDescription>
              </div>
              {!isMeaningfulEmpty && (
                <ExportButton
                  variant="icon"
                  onExport={(format) => exportWidget('meaningful_conversations', format)}
                  disabled={!sessionMetrics.data}
                  isExporting={isExporting}
                />
              )}
            </div>
          </CardHeader>
          <CardContent>
            {sessionMetrics.isLoading && (
              <div className="flex items-center gap-12">
                <Skeleton className="size-52 shrink-0 rounded-full" />
                <div className="flex flex-1 flex-col gap-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-5 w-full" />
                  ))}
                </div>
              </div>
            )}

            {!sessionMetrics.isLoading && sessionMetrics.error && (
              <SectionError onRetry={sessionMetrics.onRefetch} t={t} />
            )}

            {!sessionMetrics.isLoading && !sessionMetrics.error && !meaningfulData && (
              <SectionEmptyState t={t} />
            )}

            {!sessionMetrics.isLoading && !sessionMetrics.error && meaningfulData && (
              <div className="flex items-center gap-12">
                {/* Radial Chart */}
                <div className="flex shrink-0 flex-col items-center">
                  <div className="size-52">
                    <ChartContainer
                      config={{
                        meaningful: { label: t('analytics.meaningful'), color: 'var(--chart-1)' },
                      }}
                      className="size-full [&_.recharts-radial-bar-background-sector]:fill-sidebar-border"
                    >
                      <RadialBarChart
                        data={[{ meaningful: meaningfulData.percentage, fill: 'var(--chart-1)' }]}
                        startAngle={90}
                        endAngle={-270}
                        innerRadius={65}
                        outerRadius={95}
                      >
                        <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                        <RadialBar dataKey="meaningful" cornerRadius={10} background />
                        <PolarRadiusAxis tick={false} tickLine={false} axisLine={false}>
                          <Label
                            content={({ viewBox }) => {
                              if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                                return (
                                  <text
                                    x={viewBox.cx}
                                    y={viewBox.cy}
                                    textAnchor="middle"
                                    dominantBaseline="middle"
                                  >
                                    <tspan
                                      x={viewBox.cx}
                                      y={viewBox.cy}
                                      className="fill-foreground text-4xl font-bold"
                                    >
                                      {meaningfulData.percentage}%
                                    </tspan>
                                    <tspan
                                      x={viewBox.cx}
                                      y={(viewBox.cy || 0) + 30}
                                      className="fill-foreground-muted text-xs"
                                    >
                                      {formatCompactNumber(meaningfulData.meaningful)}/
                                      {formatCompactNumber(meaningfulData.total)}
                                    </tspan>
                                  </text>
                                );
                              }
                            }}
                          />
                        </PolarRadiusAxis>
                      </RadialBarChart>
                    </ChartContainer>
                  </div>
                  {comparison.isComparing && (
                    <PercentChangeIndicator
                      current={
                        meaningfulData.total > 0
                          ? meaningfulData.meaningful / meaningfulData.total
                          : null
                      }
                      previous={
                        comparisonSessionMetrics?.total_conversations &&
                        comparisonSessionMetrics.total_conversations > 0
                          ? (comparisonSessionMetrics.meaningful_conversations ?? 0) /
                            comparisonSessionMetrics.total_conversations
                          : null
                      }
                      isLoading={comparison.sessionMetrics.isLoading}
                      comparisonLabel={comparisonLabel}
                      className="mt-1"
                    />
                  )}
                </div>

                {/* Breakdown Table */}
                <div className="flex max-w-[50%] flex-1 flex-col gap-3">
                  <ConversationStatRow
                    label={t('analytics.meaningful')}
                    value={meaningfulData.meaningful.toLocaleString()}
                    tooltip={t('analytics.meaningful_tooltip')}
                    bold
                  />
                  <div className="ml-2 flex flex-col gap-3 border-l border-stroke pl-4">
                    <ConversationStatRow
                      label={t('analytics.overview_new.workflow_conv')}
                      value={meaningfulData.workflow.toLocaleString()}
                      tooltip={t('analytics.overview_new.workflow_conv_tooltip')}
                    />
                    <ConversationStatRow
                      label={t('analytics.overview_new.genai_conv')}
                      value={meaningfulData.genAi.toLocaleString()}
                      tooltip={t('analytics.overview_new.genai_conv_tooltip')}
                    />
                  </div>
                  <ConversationStatRow
                    label={t('analytics.non_meaningful')}
                    value={meaningfulData.nonMeaningful.toLocaleString()}
                  />
                  <ConversationStatRow
                    label={t('analytics.total_conversations')}
                    value={meaningfulData.total.toLocaleString()}
                    bold
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Time-Based Performance Card */}
        <Card className="rounded-lg bg-background text-foreground shadow-none">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex flex-col gap-2">
                <CardTitle>{t('analytics.overview_new.time_based_performance')}</CardTitle>
                <CardDescription>{dateRange}</CardDescription>
              </div>
              {!isTimeBasedEmpty && (
                <ExportButton
                  variant="icon"
                  onExport={(format) => exportWidget('time_based_performance', format)}
                  disabled={!responseTimes.data && !sessionMetrics.data}
                  isExporting={isExporting}
                />
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <DurationMetricSection
              titleKey="analytics.average_response_time"
              data={responseTimeData}
              t={t}
              isLoading={responseTimes.isLoading}
              error={!!responseTimes.error}
              onRetry={responseTimes.onRefetch}
              comparison={
                comparison.isComparing
                  ? {
                      current: responseTimes.data?.avg_response_time ?? null,
                      previous: comparison.responseTimes.data?.avg_response_time ?? null,
                      isLoading: comparison.responseTimes.isLoading,
                      label: comparisonLabel,
                    }
                  : undefined
              }
            />
            <DurationMetricSection
              titleKey="analytics.median_session_length"
              data={sessionLengthData}
              t={t}
              isLoading={sessionMetrics.isLoading}
              error={!!sessionMetrics.error}
              onRetry={sessionMetrics.onRefetch}
              comparison={
                comparison.isComparing
                  ? {
                      current: currentSessionLengthMinutes,
                      previous: comparisonSessionLengthMinutes,
                      isLoading: comparison.sessionMetrics.isLoading,
                      label: comparisonLabel,
                    }
                  : undefined
              }
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
